import {BaseQueryFn, FetchArgs, FetchBaseQueryError} from "@reduxjs/toolkit/query";
import axios, {AxiosError, AxiosRequestConfig} from "axios";
import {properties} from "../properties";
import {BaseResponse} from "../models/response/BaseResponse";
// import {descriptionAlertSlice, AlertTypeEnum} from '../share';
import {SessionState, logout, setAccessToken} from './slices/sessionSlice';
import {Mutex} from 'async-mutex';

// Create a new mutex
const mutex = new Mutex();

const CURRENT_USER_URL = '/current-users';

const axiosBaseQuery = (): BaseQueryFn<
    {
        url: string;
        method: AxiosRequestConfig['method'];
        data?: AxiosRequestConfig['data'];
    }
> => async ({url, method, data}, api) => {
    try {

        const token = (api.getState().session as SessionState)?.accessToken

        if (!token && url.startsWith(CURRENT_USER_URL)) {
            return {
                error: {
                    status: 401,
                    data: 'Authorization header required',
                }
            };
        }

        let headers = {};
        if (token) {
            headers = {
                Authorization: `Bearer ${token}`,
                ...headers,
            }
        }

        const response = await axios({
            url: properties.apiDomain + url,
            method,
            data,
            headers,
        });
        const responseData: BaseResponse<any> = response.data

        if (response.status >= 200 && response.status < 300) {
            return { data: responseData }
        } else {

            console.error("Axios response error:", responseData)

            // const alertError = (message: string) => {
            //     api.dispatch(descriptionAlertSlice.actions.open({type: AlertTypeEnum.ERROR, title: "Error", body: message, open: true}));
            // }
            // alertError(responseData.data!.message ?? responseData.message)
            return {data: null}
        }
    } catch (axiosError) {

        let err = axiosError as AxiosError

        if (err.response?.status === 401) {
            console.warn("Axios error: 401", axiosError)
        } else {
            console.error("Axios error:", axiosError)
            // const alertError = (message: string) => {
            //     api.dispatch(descriptionAlertSlice.actions.open({type: AlertTypeEnum.ERROR, title: "Error", body: message, open: true}));
            // }
            // alertError(err.response?.data?.message ?? err.message)
        }

        return {error: {status: err.response?.status, data: err.response?.data}}
    }
};

const baseQuery = axiosBaseQuery()

export const authBaseQuery = (): BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> => async (args, api, extraOptions) => {

    // wait until the mutex is available without locking it
    await mutex.waitForUnlock();

    let result = await baseQuery(args, api, extraOptions);

    if (!result
        || ((result.error?.data as any)?.message === 'Authorization header required'
            || result.error?.status === 401) && args.url !== '/auth/login') {
        if (!mutex.isLocked()) {

            const release = await mutex.acquire();
            let success = false;

            try {
                const refreshResult = await baseQuery(
                    {
                        url: '/auth/refresh',
                        method: 'POST',
                    },
                    api,
                    extraOptions
                );

                if (refreshResult.data) {
                    await api.dispatch(setAccessToken(refreshResult.data.access_token));
                    success = true;
                } else {
                    api.dispatch(logout());
                    window.location.href = '/401';
                }
            } finally {
                // release must be called once the mutex should be released again.
                release();
            }

            if (success) {
                // Retry the initial query
                result = await baseQuery(args, api, extraOptions);
            }
        } else {
            // wait until the mutex is available without locking it
            await mutex.waitForUnlock();
            result = await baseQuery(args, api, extraOptions);
        }
    }

    return result;
};
